import { CONTACT_TYPES, formatDate, formatPhone, type ContactType } from '@erp/shared';
import { useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, Plus, Search, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router';

import { TagChip } from '../../components/contacts/tag-chip.tsx';
import { Alert } from '../../components/ui/alert.tsx';
import { Badge } from '../../components/ui/badge.tsx';
import { Button, buttonVariants } from '../../components/ui/button.tsx';
import { Input, Select } from '../../components/ui/field.tsx';
import { Loading } from '../../components/ui/skeleton.tsx';
import { api, errorMessage } from '../../lib/api.ts';
import { cn } from '../../lib/cn.ts';
import {
  contactKeys,
  useAssignees,
  useChannels,
  useContacts,
  useTags,
  type ContactFilters,
} from '../../lib/contacts-api.ts';
import { useCan, useSession } from '../../lib/session.ts';
import { useToast } from '../../lib/toast.tsx';

const PAGE_SIZE = 25;
const FILTER_KEYS = [
  'q',
  'type',
  'tagId',
  'channelId',
  'assignedMembershipId',
  'createdFrom',
  'createdTo',
  'noticePending',
  'page',
] as const;

/** Contacts: search, filters and bulk tagging. Filters live in the URL (back button, links). */
export function ContactsPage() {
  const { data: session } = useSession();
  const [params, setParams] = useSearchParams();
  const filters: ContactFilters = Object.fromEntries(
    FILTER_KEYS.flatMap((k) =>
      params.get(k) ? [[k, k === 'page' ? Number(params.get(k)) : params.get(k)]] : [],
    ),
  );
  const page = filters.page ?? 1;
  const contacts = useContacts({ ...filters, page });
  const tags = useTags();
  const channels = useChannels();
  const assignees = useAssignees();
  const canDelete = useCan('records.delete');
  const canEdit = useCan('records.edit');
  const [search, setSearch] = useState(filters.q ?? '');
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const setFilter = (key: (typeof FILTER_KEYS)[number], value: string | undefined) => {
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        if (value) next.set(key, value);
        else next.delete(key);
        if (key !== 'page') next.delete('page');
        return next;
      },
      { replace: true },
    );
  };

  // Search as you type, without a request per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => {
      if ((params.get('q') ?? '') !== search.trim()) setFilter('q', search.trim() || undefined);
    }, 300);
    return () => {
      clearTimeout(timer);
    };
    // setFilter changes every render; the search text is what matters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const total = contacts.data?.total ?? 0;
  const items = contacts.data?.items ?? [];
  const from = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const to = Math.min(page * PAGE_SIZE, total);

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Contactos</h1>
          <p className="text-sm text-muted-foreground">
            Clientes, propietarios y leads de tu cartera.
          </p>
        </div>
        <div className="flex gap-2">
          {canDelete && (
            <Link to="/contactos/papelera" className={buttonVariants({ variant: 'ghost' })}>
              <Trash2 /> Papelera
            </Link>
          )}
          {canEdit && (
            <Link to="/contactos/nuevo" className={buttonVariants({ variant: 'brand' })}>
              <Plus /> Nuevo contacto
            </Link>
          )}
        </div>
      </div>

      <div className="space-y-3 rounded-xl border border-border bg-surface p-3 sm:p-4">
        <div className="relative">
          <Search
            className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden="true"
          />
          <Input
            type="search"
            aria-label="Buscar por nombre, teléfono, correo o DUI"
            placeholder="Buscar por nombre, teléfono, correo o DUI"
            className="pl-9"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
            }}
          />
        </div>
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
          <Select
            aria-label="Papel"
            value={filters.type ?? ''}
            onChange={(e) => {
              setFilter('type', e.target.value || undefined);
            }}
          >
            <option value="">Todos los papeles</option>
            {(Object.entries(CONTACT_TYPES) as [ContactType, string][]).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>
          <Select
            aria-label="Etiqueta"
            value={filters.tagId ?? ''}
            onChange={(e) => {
              setFilter('tagId', e.target.value || undefined);
            }}
          >
            <option value="">Todas las etiquetas</option>
            {(tags.data ?? []).map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </Select>
          <Select
            aria-label="Canal"
            value={filters.channelId ?? ''}
            onChange={(e) => {
              setFilter('channelId', e.target.value || undefined);
            }}
          >
            <option value="">Todos los canales</option>
            {(channels.data ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>
          <Select
            aria-label="Agente"
            value={filters.assignedMembershipId ?? ''}
            onChange={(e) => {
              setFilter('assignedMembershipId', e.target.value || undefined);
            }}
          >
            <option value="">Todos los agentes</option>
            {(assignees.data ?? []).map((m) => (
              <option key={m.membershipId} value={m.membershipId}>
                {m.name}
              </option>
            ))}
          </Select>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <label className="flex items-center gap-2">
            Alta desde
            <Input
              type="date"
              className="w-auto"
              value={filters.createdFrom ?? ''}
              onChange={(e) => {
                setFilter('createdFrom', e.target.value || undefined);
              }}
            />
          </label>
          <label className="flex items-center gap-2">
            hasta
            <Input
              type="date"
              className="w-auto"
              value={filters.createdTo ?? ''}
              onChange={(e) => {
                setFilter('createdTo', e.target.value || undefined);
              }}
            />
          </label>
          <label className="flex min-h-11 items-center gap-2">
            <input
              type="checkbox"
              className="size-5"
              checked={filters.noticePending === 'true'}
              onChange={(e) => {
                setFilter('noticePending', e.target.checked ? 'true' : undefined);
              }}
            />
            Pendientes de aviso de privacidad
          </label>
        </div>
      </div>

      {selected.size > 0 && canEdit && (
        <BulkTagBar
          ids={[...selected]}
          onDone={() => {
            setSelected(new Set());
          }}
        />
      )}

      {contacts.isPending ? (
        <Loading />
      ) : contacts.error ? (
        <Alert tone="danger">{errorMessage(contacts.error)}</Alert>
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border p-8 text-center">
          <p className="font-medium">
            {Object.keys(filters).length > 0
              ? 'Ningún contacto coincide.'
              : 'Todavía no hay contactos.'}
          </p>
          <p className="text-sm text-muted-foreground">
            {Object.keys(filters).length > 0
              ? 'Prueba con otra búsqueda o quita filtros.'
              : 'Crea el primero o impórtalos desde Excel.'}
          </p>
        </div>
      ) : (
        <>
          <ul
            className={cn(
              'divide-y divide-border rounded-xl border border-border bg-surface',
              contacts.isFetching && 'opacity-70',
            )}
            aria-busy={contacts.isFetching}
          >
            {items.map((c) => (
              <li key={c.id} className="flex items-start gap-3 p-3 sm:p-4">
                {canEdit && (
                  <input
                    type="checkbox"
                    className="mt-1 size-5 shrink-0"
                    aria-label={`Seleccionar ${c.name}`}
                    checked={selected.has(c.id)}
                    onChange={(e) => {
                      setSelected((current) => {
                        const next = new Set(current);
                        if (e.target.checked) next.add(c.id);
                        else next.delete(c.id);
                        return next;
                      });
                    }}
                  />
                )}
                <div className="min-w-0 flex-1 space-y-1">
                  <Link
                    to={`/contactos/${c.id}`}
                    className="font-medium text-primary hover:underline"
                  >
                    {c.name}
                  </Link>
                  <p className="text-sm text-muted-foreground">
                    {[c.phone && formatPhone(c.phone), c.email].filter(Boolean).join(' · ') ||
                      'Sin teléfono ni correo'}
                  </p>
                  <div className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                    {c.types.map((t) => (
                      <Badge key={t} tone="info">
                        {CONTACT_TYPES[t]}
                      </Badge>
                    ))}
                    {c.tags.map((t) => (
                      <TagChip key={t.id} name={t.name} color={t.color} />
                    ))}
                    {c.flags.limited && <Badge tone="warning">Tratamiento limitado</Badge>}
                    {c.flags.rectification && <Badge tone="warning">En rectificación</Badge>}
                    {c.flags.marketingOptOut && <Badge>Sin marketing</Badge>}
                    {c.flags.noticePending && <Badge tone="info">Aviso pendiente</Badge>}
                    {c.district && <span>{c.district.name}</span>}
                    <span>· {c.assignedTo.name}</span>
                    <span className="hidden sm:inline">
                      · Alta {formatDate(c.createdAt, session?.tenant.timezone)}
                    </span>
                  </div>
                </div>
              </li>
            ))}
          </ul>
          <nav aria-label="Páginas" className="flex items-center justify-between text-sm">
            <p className="text-muted-foreground">
              {from}–{to} de {total}
            </p>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="secondary"
                disabled={page <= 1}
                onClick={() => {
                  setFilter('page', String(page - 1));
                }}
              >
                <ChevronLeft /> Anterior
              </Button>
              <Button
                size="sm"
                variant="secondary"
                disabled={to >= total}
                onClick={() => {
                  setFilter('page', String(page + 1));
                }}
              >
                Siguiente <ChevronRight />
              </Button>
            </div>
          </nav>
        </>
      )}
    </div>
  );
}

function BulkTagBar({ ids, onDone }: { ids: string[]; onDone: () => void }) {
  const tags = useTags();
  const client = useQueryClient();
  const notify = useToast();
  const [tagId, setTagId] = useState('');
  const [busy, setBusy] = useState(false);

  const apply = async (action: 'add' | 'remove') => {
    setBusy(true);
    try {
      const result = await api<{ updated: number; skipped: number }>('/contacts/tags/bulk', {
        method: 'POST',
        body: { contactIds: ids, tagId, action },
      });
      await client.invalidateQueries({ queryKey: contactKeys.all });
      notify(
        `${action === 'add' ? 'Etiqueta agregada' : 'Etiqueta quitada'} en ${String(result.updated)} contacto${result.updated === 1 ? '' : 's'}${result.skipped > 0 ? ` (${String(result.skipped)} fuera de tu alcance)` : ''}.`,
        'success',
      );
      onDone();
    } catch (error) {
      notify(errorMessage(error), 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      role="region"
      aria-label="Acciones con los seleccionados"
      className="flex flex-col gap-2 rounded-xl border border-border bg-soft p-3 sm:flex-row sm:items-center"
    >
      <p className="text-sm font-medium text-soft-foreground">
        {ids.length} seleccionado{ids.length === 1 ? '' : 's'}
      </p>
      <Select
        aria-label="Etiqueta para los seleccionados"
        className="sm:w-56"
        value={tagId}
        onChange={(e) => {
          setTagId(e.target.value);
        }}
      >
        <option value="">Elige una etiqueta</option>
        {(tags.data ?? []).map((t) => (
          <option key={t.id} value={t.id}>
            {t.name}
          </option>
        ))}
      </Select>
      <div className="flex gap-2">
        <Button size="sm" disabled={!tagId} loading={busy} onClick={() => void apply('add')}>
          Agregar
        </Button>
        <Button
          size="sm"
          variant="secondary"
          disabled={!tagId || busy}
          onClick={() => void apply('remove')}
        >
          Quitar
        </Button>
        <Button size="sm" variant="ghost" onClick={onDone}>
          Cancelar
        </Button>
      </div>
    </div>
  );
}
